import type {
  ColorByCategory,
  ColorByRule,
} from 'components/color-by-attribute-modal';
import {
  deriveValues,
  refreshRule,
  resolveColors,
  type ColorableItem,
  type SectionColorRule,
} from './attribute-colors';

/**
 * Swatches for the grids on a section. Colour is a display choice, so it lives in the
 * rail as a colour-by rule over grid metadata rather than a fixed per-grid list —
 * deriving values from the section, keeping edits, defaulting anything new.
 */
const BASE_GRID_COLOR = '#0f62fe';

const GRID_COLORS = [
  '#ff832b',
  '#24a148',
  '#8a3ffc',
  '#d12771',
  '#007d79',
  '#4589ff',
  '#ee5396',
  '#08bdba',
  '#b28600',
  '#9f1853',
  '#a56eff',
  '#6fdc8c',
];

/** The metadata a colour-by rule can read. Kept to what both sources carry. */
export interface GridIdentity {
  gridId: string;
  geologicInterval: string | null;
  gridName: string;
  dataSource: string | null;
  dataOwner: string | null;
  /**
   * The interval's stored display colour, where the source knows it. The default
   * swatch, so a stack opens in the colours the catalogue already gave it — the
   * palette only covers what has no stored colour.
   */
  displayColor?: string | null;
}

/**
 * The attributes a grid colour rule can be written over. Grid metadata, not a list
 * redefined for the cross-section — the same values the Structure Grids catalogue
 * shows.
 */
export const GRID_COLOR_CATEGORIES: ColorByCategory[] = [
  { id: 'geologicInterval', label: 'Geologic Interval' },
  { id: 'gridName', label: 'Grid Name' },
  { id: 'dataSource', label: 'Data Source' },
  { id: 'dataOwner', label: 'Data Owner' },
];

/**
 * The surfaces a grid colour rule paints. Only the section for now: the map's grid
 * polygons are painted by the structure-grid layer, and offering a target that
 * changes nothing would be a rule that lies about what it does.
 */
export const GRID_COLOR_TARGETS = [
  { id: 'crossSection', label: 'Cross Section' },
];

/**
 * The default swatch for the grid at this position in the stack. The first keeps
 * Carbon blue, which is what the map already draws a structure grid in.
 *
 * @param {number} index - The position in the stack, or in the rule's value list.
 * @return {string} A hex colour.
 */
export const defaultGridColor = (index: number): string => {
  if (index === 0) {
    return BASE_GRID_COLOR;
  }
  return GRID_COLORS[(index - 1) % GRID_COLORS.length];
};

/**
 * A grid's value for one of the rule attributes.
 *
 * @param {GridIdentity} grid - The grid.
 * @param {string} attribute - The attribute id.
 * @return {string | null} The value, or null where the grid has none.
 */
export const gridAttributeValue = (
  grid: GridIdentity,
  attribute: string
): string | null => {
  switch (attribute) {
    case 'geologicInterval':
      return grid.geologicInterval;
    case 'gridName':
      return grid.gridName;
    case 'dataSource':
      return grid.dataSource;
    case 'dataOwner':
      return grid.dataOwner;
    default:
      return null;
  }
};

/**
 * The distinct values the grids on the section have for an attribute, in stack
 * order — which is the order the rule lists them and hands out default colours in.
 *
 * @param {Array} grids - The grids on the section, in stack order.
 * @param {string} attribute - The attribute id.
 * @return {Array} The values, without duplicates or blanks.
 */
export const deriveGridColorValues = (
  grids: GridIdentity[],
  attribute: string
): string[] => deriveValues(grids.map(asColorable), attribute);

/**
 * A grid as something a rule can colour: its id, and its attributes by rule name.
 *
 * @param {GridIdentity} grid - The grid.
 * @return {ColorableItem} The grid, in the shared shape.
 */
const asColorable = (grid: GridIdentity): ColorableItem => ({
  id: grid.gridId,
  attributes: {
    geologicInterval: grid.geologicInterval,
    gridName: grid.gridName,
    dataSource: grid.dataSource,
    dataOwner: grid.dataOwner,
  },
  storedColor: grid.displayColor ?? null,
});

/**
 * The stored display colour for a rule value, when the rule reads intervals — the
 * one attribute whose values carry a colour of their own.
 *
 * @param {GridIdentity[]} grids - The grids on the section.
 * @param {string} attribute - The rule's attribute.
 * @param {string} value - The value needing a default.
 * @return {string | null} The stored colour, or null to fall to the palette.
 */
const storedColorFor = (
  grids: GridIdentity[],
  attribute: string,
  value: string
): string | null => {
  if (attribute !== 'geologicInterval') {
    return null;
  }
  return (
    grids.find(
      (grid) => grid.geologicInterval === value && Boolean(grid.displayColor)
    )?.displayColor ?? null
  );
};

/**
 * Brings a rule up to date with the grids on the section: its values are re-derived
 * from the current stack, any swatch the user edited is kept, and a value the rule
 * has not seen before gets the default for its position.
 *
 * This is what makes adding a grid a display change rather than a re-colouring —
 * the grids already on the plot keep the colours they had.
 *
 * @param {SectionColorRule} rule - The rule as it stands.
 * @param {Array} grids - The grids on the section, in stack order.
 * @return {SectionColorRule} The rule, with its values and colours filled in.
 */
export const refreshGridColorRule = (
  rule: SectionColorRule,
  grids: GridIdentity[]
): SectionColorRule =>
  refreshRule(
    rule,
    grids.map(asColorable),
    (index, value) =>
      storedColorFor(grids, rule.attribute, value) ?? defaultGridColor(index)
  );

/**
 * The colour each grid draws in.
 *
 * Rules are read in order and the last one that has something to say wins, so a
 * rule added later layers over an earlier one. A grid no rule covers falls back to
 * its position in the stack, so the plot is never uncoloured.
 *
 * @param {Array} grids - The grids on the section, in stack order.
 * @param {Array} rules - The rail's colour-by rules.
 * @return {Map} Grid id to hex colour.
 */
export const resolveGridColors = (
  grids: GridIdentity[],
  rules: ColorByRule[]
): Map<string, string> => {
  // With no rule painting the section, every horizon takes the one blue the map draws
  // structure grids in, as wells fall to their navy. The stored interval colours belong
  // to the rule: with it off, a stack coloured by interval reads as though it were on.
  const painted = rules.some(
    (rule) => rule.enabled && rule.applyTo.crossSection
  );
  return resolveColors(
    grids.map(asColorable),
    rules,
    'crossSection',
    painted
      ? (index, item) => item.storedColor ?? defaultGridColor(index)
      : () => BASE_GRID_COLOR
  );
};
